import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Stack,
  Grid,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Drawer,
  Divider,
  Chip,
  CircularProgress,
  InputAdornment,
  Switch,
} from '@mui/material';

// Icons
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import EventNoteIcon from '@mui/icons-material/EventNote';
import BadgeIcon from '@mui/icons-material/Badge';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import StorefrontIcon from '@mui/icons-material/Storefront';
import StarRateIcon from '@mui/icons-material/StarRate';
import PercentIcon from '@mui/icons-material/Percent';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HistoryIcon from '@mui/icons-material/History';
import TimerIcon from '@mui/icons-material/Timer';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import './Administrator.css';
import AppPagination from '../../components/base/AppPagination';
import { usePagination } from '../../hooks/usePagination';
import ConfirmDialog from '../../components/base/ConfirmDialog';
import AppSearch from '../../components/base/AppSearch';
import { DndContext, closestCenter } from '@dnd-kit/core';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';

import { CSS } from '@dnd-kit/utilities';
const DetailItem = ({ label, value, icon, color }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 1.2,
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      {icon &&
        React.cloneElement(icon, {
          sx: { color: '#A3AED0', fontSize: '18px' },
        })}
      <Typography sx={{ color: '#A3AED0', fontSize: '14px', fontWeight: 500 }}>
        {label}
      </Typography>
    </Stack>
    <Typography
      sx={{
        color: color || '#2B3674',
        fontSize: '14px',
        fontWeight: 700,
        textAlign: 'right',
        ml: 2,
      }}
    >
      {value || '—'}
    </Typography>
  </Box>
);

const Administrator = () => {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [openAddDrawer, setOpenAddDrawer] = useState(false);
  const [addFile, setAddFile] = useState(null);
  const [addPreview, setAddPreview] = useState(null);
  const [newData, setNewData] = useState({
    nom_user: '',
    prenom_user: '',
    email_user: '',
    password_user: '', // Doit correspondre au backend
    addresse_user: '',
    ville_user: '',
    cp_user: '',
    province_user: '',
    phone_user: '',
    role_user: '2', // '1' ou '2' selon tes règles Laravel
    etat_user: 'active',
  });
  const [openAddIntervenantDrawer, setOpenAddIntervenantDrawer] =
    useState(false);
  const [intervenantFile, setIntervenantFile] = useState(null);
  const [intervenantPreview, setIntervenantPreview] = useState(null);
  const [intervenantData, setIntervenantData] = useState({
    nom_user: '',
    prenom_user: '',
    email_user: '',
    password_user: '',
    addresse_user: '',
    ville_user: '',
    cp_user: '',
    province_user: '',
    phone_user: '',
    description_user: '',
    title_user: '',
    main_speciality_user: '',
    lien_boutique: '',
    etat_user: 'active',
  });
  // 1. D'abord l'état de recherche
  const [searchTerm, setSearchTerm] = useState('');

  // 2. Ensuite le filtrage (avec useMemo bien importé)
  const filteredAdmins = useMemo(() => {
    return admins.filter((admin) => {
      const searchStr = searchTerm.toLowerCase();
      return (
        admin.nom_user?.toLowerCase().includes(searchStr) ||
        admin.prenom_user?.toLowerCase().includes(searchStr) ||
        admin.email_user?.toLowerCase().includes(searchStr)
      );
    });
  }, [admins, searchTerm]);

  // 3. Enfin la pagination qui utilise les résultats filtrés
  const itemsPerPage = 8;
  // Ajoutez totalItems ici si votre hook le retourne
  const { next, currentData, currentPage, count } = usePagination(
    filteredAdmins,
    itemsPerPage
  );
  // On récupère la base
  const baseUrl = process.env.REACT_APP_API_URL;

  // On définit le chemin spécifique pour les admins/intervenants
  const storageUrl = `${baseUrl}/storage/workshop`;

  // Tes autres constantes
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');

  const getFullImageUrl = (photoPath) => {
    if (!photoPath) return null;
    if (photoPath.startsWith('blob:') || photoPath.startsWith('http'))
      return photoPath;
    return `${storageUrl}/${photoPath}`;
  };

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/admin/la/list_admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data?.status === 'success')
        setAdmins(response.data.result || []);
    } catch (err) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token]);

  useEffect(() => {
    if (user) fetchAdmins();
  }, [user, fetchAdmins]);

  const handleAddSubmit = async () => {
    if (!addFile) {
      toast.error('La photo est obligatoire');
      return;
    }

    setIsSubmitting(true);
    const formData = new FormData();

    // On ajoute tous les champs texte
    Object.keys(newData).forEach((key) => {
      formData.append(key, newData[key]);
    });

    // On ajoute le fichier image
    formData.append('photo_user', addFile);

    try {
      const response = await axios.post(
        `${apiUrl}/admin/aa/add_admin`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.status === 'success') {
        toast.success('Administrateur ajouté !');
        setOpenAddDrawer(false);
        setAddFile(null);
        setAddPreview(null);
        setNewData({
          nom_user: '',
          prenom_user: '',
          email_user: '',
          password_user: '',
          addresse_user: '',
          ville_user: '',
          cp_user: '',
          province_user: '',
          phone_user: '',
          role_user: '2',
          etat_user: 'active',
        });
        fetchAdmins();
      }
    } catch (err) {
      const errors = err.response?.data?.error;
      if (typeof errors === 'object') {
        Object.values(errors).forEach((msg) => toast.error(msg[0]));
      } else {
        toast.error(err.response?.data?.error || "Erreur lors de l'ajout");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // add intervneant
  const handleAddIntervenantSubmit = async () => {
    setIsSubmitting(true);
    const formData = new FormData();

    // On ajoute tous les champs texte de l'intervenant
    Object.keys(intervenantData).forEach((key) => {
      formData.append(key, intervenantData[key]);
    });

    // Photo obligatoire selon ton controller (ou recommandée)
    if (intervenantFile) {
      formData.append('photo_user', intervenantFile);
    }

    try {
      const response = await axios.post(
        `${apiUrl}/admin/intervenant/a/add_intervenant`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.message === 'intervenant created successfully') {
        toast.success('Intervenant et compte Zoom créés !');
        setOpenAddIntervenantDrawer(false);
        setIntervenantFile(null);
        setIntervenantPreview(null);
        // Reset du formulaire
        setIntervenantData({
          nom_user: '',
          prenom_user: '',
          email_user: '',
          password_user: '',
          addresse_user: '',
          ville_user: '',
          cp_user: '',
          province_user: '',
          phone_user: '',
          description_user: '',
          title_user: '',
          main_speciality_user: '',
          lien_boutique: '',
          etat_user: 'active',
        });
        fetchAdmins();
      }
    } catch (err) {
      // 1. On récupère les données de l'erreur
      const errorData = err.response?.data;

      // 2. Si c'est une erreur de validation Laravel (souvent dans .errors ou .error)
      if (errorData?.error && typeof errorData.error === 'object') {
        // On boucle sur chaque champ en erreur pour afficher le premier message de chaque
        Object.values(errorData.error).forEach((messages) => {
          toast.error(Array.isArray(messages) ? messages[0] : messages);
        });
      }
      // 3. Si l'API envoie un message direct en string
      else if (typeof errorData?.error === 'string') {
        toast.error(errorData.error);
      } else if (typeof errorData?.message === 'string') {
        toast.error(errorData.message);
      }
      // 4. Erreur par défaut si on ne comprend pas la structure
      else {
        toast.error("Erreur lors de la création de l'intervenant");
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  // --- NOUVELLE FONCTION DELETE ---

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Étape A : Ouvre le popup et mémorise l'ID de l'admin à supprimer
  const handleOpenDeleteDialog = (admin) => {
    setAdminToDelete(admin); // Ici admin contient nom_user, prenom_user, id_user, etc.
    setDeleteDialogOpen(true);
  };

  // Étape B : Appelé quand l'utilisateur clique sur "Confirmer" dans le popup
  const handleConfirmDelete = async () => {
    if (!adminToDelete) return;

    setIsDeleting(true);
    try {
      // On utilise l'id contenu dans l'objet
      const response = await axios.delete(
        `${apiUrl}/admin/delete/${adminToDelete.id_user}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.status === 'success') {
        toast.success('Administrateur supprimé avec succès');
        fetchAdmins();
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || 'Erreur lors de la suppression'
      );
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setAdminToDelete(null);
    }
  };

  const handleOpenView = (admin) => {
    if (!admin) return;

    setSelectedAdmin(admin);
    setEditData(admin);
    setPreviewUrl(admin?.photo_user || null);
    setIsEditMode(false);

    // IMPORTANT : reset preview state if needed
    setSelectedFile(null);

    setOpenDrawer(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({
      ...prev,
      [name]: name === 'availability_user' ? Number(value) : value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleUpdateSubmit = async () => {
    setIsSubmitting(true);
    const formData = new FormData();

    // SUPPRIME CETTE LIGNE : formData.append('_method', 'POST');
    // Elle est inutile car axios.post envoie déjà en POST.

    const fieldsToSkip = [
      'id_user',
      'date_inscrip_user',
      'last_cnx_user',
      'nbr_view',
      'total_reviews_user',
    ];

    Object.keys(editData).forEach((key) => {
      // On ne skip plus photo_user ici car on veut envoyer les autres données
      if (
        !fieldsToSkip.includes(key) &&
        editData[key] !== null &&
        editData[key] !== undefined
      ) {
        formData.append(key, editData[key]);
      }
    });

    // C'est ici que la magie opère
    if (selectedFile) {
      formData.append('photo_user', selectedFile);
    }

    try {
      const response = await axios.post(
        `${apiUrl}/admin/update/u/user/${selectedAdmin.id_user}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.status === 'success') {
        toast.success('Profil mis à jour');
        setOpenDrawer(false);
        fetchAdmins();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur de mise à jour');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleInfo = (role) => {
    switch (Number(role)) {
      case 1:
        return { label: 'Super Admin', bgcolor: '#EAE6FF', color: '#4318FF' };
      case 2:
        return { label: 'Admin', bgcolor: '#E6F9F0', color: '#05CD99' };
      case 3:
        return { label: 'Intervenant', bgcolor: '#FFF5E6', color: '#FFB547' };
      default:
        return { label: 'Membre', bgcolor: '#F4F7FE', color: '#2B3674' };
    }
  };
  const [loadingId, setLoadingId] = useState(null);
  const toggleAvailability = async (admin) => {
    const adminId = admin.id_user;
    // On force le typage en Number pour éviter les erreurs de comparaison "1" vs 1
    const oldStatus = Number(admin.availability_user);
    const newStatus = oldStatus === 1 ? 0 : 1;

    setLoadingId(adminId);

    // 1. UPDATE OPTIMISTE : On change l'UI immédiatement
    setAdmins((prevAdmins) => {
      // .map() crée un nouveau tableau, ce qui déclenche le refresh de React
      return prevAdmins.map((item) => {
        if (item.id_user === adminId) {
          return { ...item, availability_user: newStatus };
        }
        return item;
      });
    });

    try {
      const res = await axios.post(
        `${apiUrl}/admin/intervenant/status/${adminId}`,
        { availability_user: newStatus },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // 2. SYNCHRONISATION : On confirme avec la réponse du serveur
      if (res.data?.result) {
        setAdmins((prevAdmins) =>
          prevAdmins.map((item) =>
            item.id_user === adminId
              ? {
                  ...item,
                  availability_user: Number(res.data.result.availability_user),
                }
              : item
          )
        );
      }
    } catch (err) {
      // 3. ROLLBACK : En cas d'erreur, on remet l'ancien statut
      setAdmins((prevAdmins) =>
        prevAdmins.map((item) =>
          item.id_user === adminId
            ? { ...item, availability_user: oldStatus }
            : item
        )
      );
      console.error(err);
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoadingId(null);
    }
  };

  const [openSessionsDrawer, setOpenSessionsDrawer] = useState(false);
  const [sessionUser, setSessionUser] = useState(null);

  const handleOpenSessions = (admin) => {
    setSelectedAdmin(admin);
    setOpenSessionsDrawer(true); // On ouvre uniquement celui-ci
  };
  const orderedAdmins = filteredAdmins;

  const DraggableRow = ({ admin, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({
        id: admin.id_user,
      });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <TableRow ref={setNodeRef} style={style}>
        {/* HANDLE DRAG */}
        <TableCell width={50}>
          <Box
            {...attributes}
            {...listeners}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'grab',
              color: '#A3AED0',
            }}
          >
            <DragIndicatorIcon />
          </Box>
        </TableCell>

        {children}
      </TableRow>
    );
  };

  /* google calandar  */
const handleConnectGoogle = async (adminId) => {
  const response = await axios.get(
    `${apiUrl}/intervenant/google-calendar/connect`,
    {
      params: { id_user: adminId },
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (response.data?.url) {
    const popup = window.open(
      response.data.url,
      'google-oauth',
      'width=500,height=650'
    );

    const timer = setInterval(() => {
      if (popup?.closed) {
        clearInterval(timer);
        toast.success("Connexion Google terminée");
        fetchAdmins(); // refresh status
      }
    }, 1000);
  }
};

  const handleDisconnectGoogle = async (adminId) => {
    try {
      setLoadingId(adminId);
      console.log(
        "Tentative de déconnexion de Google Calendar pour l'admin ID :",
        adminId
      );
      const response = await axios.delete(
        `${apiUrl}/admin/google-calendar/disconnect`,
        {
          params: { id_user: adminId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data?.status === 'success') {
        toast.success('Google Calendar déconnecté');

        // mise à jour optimiste UI
        setAdmins((prev) =>
          prev.map((a) =>
            a.id_user === adminId
              ? {
                  ...a,
                  google_calendar_account: {
                    ...a.google_calendar_account,
                    is_connected: false,
                  },
                }
              : a
          )
        );
      }
    } catch (err) {
      // 1. On extrait le message de l'API s'il existe, sinon on met un message par défaut
      const apiErrorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Erreur lors de la déconnexion Google Calendar';

      // 2. On affiche le vrai message dans le toast
      toast.error(apiErrorMessage);

      // On garde le console.error complet pour inspecter l'objet dans la console du navigateur
      console.error("Détail de l'erreur API :", err.response?.data);
    } finally {
      setLoadingId(null);
    }
  };
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    // Sauvegarde ancienne liste pour rollback
    const oldAdmins = [...admins];

    // Trouver les index
    const oldIndex = admins.findIndex((item) => item.id_user === active.id);

    const newIndex = admins.findIndex((item) => item.id_user === over.id);

    // Nouveau tableau réordonné
    const newArray = arrayMove(admins, oldIndex, newIndex);

    // UPDATE UI DIRECT
    setAdmins(newArray);

    try {
      // On récupère uniquement les IDs
      const orderedIds = newArray
        .filter((item) => Number(item.role_user) === 3)
        .map((item) => item.id_user);

      // API SAVE
      await axios.post(
        `${apiUrl}/admin/intervenants/update-positions`,
        {
          ordered_ids: orderedIds,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success('Ordre mis à jour');
    } catch (error) {
      // rollback si erreur
      setAdmins(oldAdmins);

      toast.error("Erreur lors de la sauvegarde de l'ordre");

      console.error(error);
    }
  };
  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 4 }}
      >
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#2B3674' }}>
          Staff & Accès
        </Typography>
        <Stack direction="row" spacing={2}>
          <AppSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Rechercher un admin..."
          />
          <Button
            variant="outlined"
            onClick={() => setOpenAddDrawer(true)}
            sx={{
              borderRadius: '12px',
              fontWeight: 700,
              borderColor: '#4318FF',
              color: '#4318FF',
            }}
          >
            + Admin
          </Button>
          <Button
            variant="contained"
            onClick={() => setOpenAddIntervenantDrawer(true)}
            sx={{
              bgcolor: '#4318FF',
              borderRadius: '20px', // Plus arrondi pour l'effet "pilule"
              px: 6, // Réduit le padding (12 était trop large)
              py: 1.5, // Ajuste le padding vertical au lieu d'une hauteur fixe
              textTransform: 'none',
              fontSize: '14px', // Taille de police standard dashboard
              fontWeight: 600, // Pour plus de lisibilité
              whiteSpace: 'nowrap', // Force le texte sur une seule ligne
              boxShadow: '0px 4px 12px rgba(67, 24, 255, 0.2)', // Optionnel: petite ombre portée
              '&:hover': {
                bgcolor: '#3311CC',
                boxShadow: 'none',
              },
            }}
          >
            + Intervenant
          </Button>
        </Stack>
      </Stack>
      <Paper elevation={0} className="admin-table-container">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell className="table-header-cell">MEMBRE</TableCell>
                <TableCell className="table-header-cell">RÔLE</TableCell>
                <TableCell className="table-header-cell">
                  DISPONIBILITÉ
                </TableCell>
                <TableCell className="table-header-cell">
                  GOOGLE CALENDAR
                </TableCell>{' '}
                {/* Nouvelle colonne */}
                <TableCell className="table-header-cell">
                  Durée de connexion
                </TableCell>
                <TableCell align="right" className="table-header-cell">
                  ACTIONS
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress size={30} sx={{ my: 2 }} />
                  </TableCell>
                </TableRow>
              ) : (
                <DndContext
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={orderedAdmins.map((a) => a.id_user)}
                    strategy={verticalListSortingStrategy}
                  >
                    {orderedAdmins.filter(Boolean).map((admin) => (
                      <DraggableRow key={admin.id_user} admin={admin}>
                        {/* USER */}
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                          >
                            <Avatar src={getFullImageUrl(admin.photo_user)} />
                            <Box>
                              <Typography>
                                {admin.nom_user} {admin.prenom_user}
                              </Typography>
                              <Typography variant="caption">
                                {admin.email_user}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* ROLE */}
                        <TableCell>
                          <Chip
                            label={getRoleInfo(admin.role_user).label}
                            sx={{
                              bgcolor: getRoleInfo(admin.role_user).bgcolor,
                              color: getRoleInfo(admin.role_user).color,
                              fontWeight: 700,
                            }}
                          />
                        </TableCell>

                        {/* STATUS */}
                        <TableCell>
                          <Switch
                            checked={Number(admin.availability_user) === 1}
                            onChange={() => toggleAvailability(admin)}
                          />
                        </TableCell>
                        {/* GOOGLE CALENDAR (Nouvelle cellule) */}
                        <TableCell>
                          {Number(admin.role_user) === 3 ? (
                            (() => {
                              const isConnected =
                                admin.google_calendar_account?.is_connected;
                              const isLoading = loadingId === admin.id_user;

                              return isConnected ? (
                                /* --- ÉTAT CONNECTÉ : L'icône de validation + le bouton Déconnecter --- */
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  {/* On utilise une icône de coche verte (CheckCircle ou Check selon tes imports) */}
                                  <CheckCircleIcon
                                    color="success"
                                    fontSize="small"
                                  />

                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    disabled={isLoading}
                                    onClick={() =>
                                      handleDisconnectGoogle(admin.id_user)
                                    }
                                    sx={{
                                      textTransform: 'none',
                                      borderRadius: '8px',
                                    }}
                                  >
                                    Déconnecter
                                  </Button>
                                </Stack>
                              ) : (
                                /* --- ÉTAT DÉCONNECTÉ : Le bouton simple pour se connecter --- */
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  disabled={isLoading}
                                  onClick={() =>
                                    handleConnectGoogle(admin.id_user)
                                  }
                                  sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    whiteSpace: 'nowrap', // Évite que le texte saute à la ligne si la cellule est petite
                                  }}
                                >
                                  Connecter Google
                                </Button>
                              );
                            })()
                          ) : (
                            /* --- HORS SUJET : Pas un intervenant --- */
                            <Typography variant="caption" color="textSecondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        {/* SESSIONS */}
                        <TableCell>
                          <Button onClick={() => handleOpenSessions(admin)}>
                            Sessions
                          </Button>
                        </TableCell>

                        {/* ACTIONS */}
                        <TableCell align="right">
                          <IconButton onClick={() => handleOpenView(admin)}>
                            <VisibilityIcon />
                          </IconButton>

                          <IconButton
                            onClick={() => handleOpenDeleteDialog(admin)}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </TableCell>
                      </DraggableRow>
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Drawer
          anchor="right"
          open={openSessionsDrawer} // Utilisation de l'état spécifique
          onClose={() => setOpenSessionsDrawer(false)}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1000,
            '& .MuiDrawer-paper': {
              width: { xs: '100%', sm: 500 },
              border: 'none',
              backgroundColor: '#F4F7FE',
            },
          }}
        >
          {selectedAdmin && (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
            >
              {/* HEADER */}
              <Box
                sx={{
                  p: 3,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: 'white',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: '#2B3674' }}
                >
                  Détails des Sessions en ligne
                </Typography>
                <IconButton onClick={() => setOpenDrawer(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* CONTENU SESSIONS */}
              <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
                {/* PETIT RÉCAP PROFIL */}
                <Box
                  sx={{
                    mb: 4,
                    p: 3,
                    borderRadius: '20px',
                    bgcolor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Avatar
                    src={getFullImageUrl(selectedAdmin.image_user)}
                    sx={{ width: 60, height: 60 }}
                  />
                  <Box>
                    <Typography sx={{ fontWeight: 800, color: '#2B3674' }}>
                      {selectedAdmin.nom_user} {selectedAdmin.prenom_user}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#A3AED0' }}>
                      {selectedAdmin.user_sessions?.length || 0} sessions
                      enregistrées
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#A3AED0',
                    fontWeight: 700,
                    mb: 2,
                    ml: 1,
                    textTransform: 'uppercase',
                  }}
                >
                  Historique
                </Typography>

                <Stack spacing={2}>
                  {selectedAdmin.user_sessions &&
                  selectedAdmin.user_sessions.length > 0 ? (
                    selectedAdmin.user_sessions.map((session) => {
                      const isActive = !session.logout_time;
                      return (
                        <Box
                          key={session.id_userSession}
                          sx={{
                            p: 2,
                            borderRadius: '16px',
                            bgcolor: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            border: isActive
                              ? '1px solid #05CD99'
                              : '1px solid transparent',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: isActive ? '#E6F9F0' : '#F4F7FE',
                                color: isActive ? '#05CD99' : '#4318FF',
                              }}
                            >
                              {isActive ? <BoltIcon /> : <HistoryIcon />}
                            </Box>
                            <Box>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  color: '#2B3674',
                                  fontSize: '0.85rem',
                                }}
                              >
                                {isActive ? 'En cours' : 'Terminée'}
                              </Typography>
                              <Typography
                                sx={{ color: '#A3AED0', fontSize: '0.75rem' }}
                              >
                                {new Date(session.login_time).toLocaleString(
                                  'fr-FR'
                                )}
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ textAlign: 'right' }}>
                            <Typography
                              sx={{ fontWeight: 800, color: '#2B3674' }}
                            >
                              {session.working_minutes || 0} min
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })
                  ) : (
                    <Typography
                      sx={{ textAlign: 'center', color: '#A3AED0', mt: 4 }}
                    >
                      Aucune session trouvée
                    </Typography>
                  )}
                </Stack>
              </Box>

              {/* FOOTER */}
              <Box sx={{ p: 3, bgcolor: 'white' }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => setOpenSessionsDrawer(false)}
                  sx={{
                    bgcolor: '#4318FF',
                    borderRadius: '12px',
                    py: 1.5,
                    fontWeight: 700,
                  }}
                >
                  Fermer
                </Button>
              </Box>
            </Box>
          )}
        </Drawer>

        {!loading &&
          filteredAdmins.length > 0 && ( // On vérifie la liste FILTRÉE
            <AppPagination
              count={count} // Vient de usePagination (calculé sur filteredAdmins)
              currentPage={currentPage}
              onChange={next} // La fonction pour changer de page
              totalItems={filteredAdmins.length}
              itemsPerPage={itemsPerPage}
            />
          )}
      </Paper>

      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1000 }}
        PaperProps={{
          className: 'drawer-paper',
          sx: { width: { xs: '100%', sm: 550 } },
        }}
      >
        {selectedAdmin && (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          >
            <Box className="drawer-header">
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, color: '#2B3674' }}
              >
                {isEditMode ? 'Modifier Profil' : 'Détails Membre'}
              </Typography>
              <IconButton onClick={() => setOpenDrawer(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
              <Paper
                elevation={0}
                sx={{ p: 4, borderRadius: '25px', textAlign: 'center', mb: 3 }}
              >
                <Avatar
                  src={getFullImageUrl(previewUrl)}
                  className="profile-avatar-large"
                  sx={{
                    width: 120,
                    height: 120,
                    mx: 'auto',
                    mb: 2,
                    border: '4px solid #F4F7FE',
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, color: '#2B3674' }}
                >
                  {editData.nom_user} {editData.prenom_user}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="center"
                  sx={{ mt: 1 }}
                >
                  <Chip
                    label={getRoleInfo(editData.role_user).label}
                    sx={{
                      fontWeight: 700,
                      bgcolor: getRoleInfo(editData.role_user).bgcolor,
                      color: getRoleInfo(editData.role_user).color,
                    }}
                  />
                  <Chip
                    icon={<StarRateIcon sx={{ '&&': { color: '#FFB547' } }} />}
                    label={`${editData.total_reviews_user || 0} Avis`}
                    sx={{
                      fontWeight: 700,
                      bgcolor: '#FFF9F0',
                      color: '#FFB547',
                    }}
                  />
                </Stack>
              </Paper>

              {isEditMode ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sx={{ textAlign: 'center', mb: 2 }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="photo-upload"
                      type="file"
                      onChange={handleFileChange}
                    />
                    <label htmlFor="photo-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<PhotoCamera />}
                      >
                        Changer la photo
                      </Button>
                    </label>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Nom"
                      name="nom_user"
                      fullWidth
                      value={editData.nom_user || ''}
                      onChange={handleEditChange}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Prénom"
                      name="prenom_user"
                      fullWidth
                      value={editData.prenom_user || ''}
                      onChange={handleEditChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Email"
                      name="email_user"
                      fullWidth
                      value={editData.email_user || ''}
                      onChange={handleEditChange}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      select
                      label="Disponibilité"
                      name="availability_user"
                      fullWidth
                      value={editData.availability_user ?? 0}
                      onChange={handleEditChange}
                      SelectProps={{ MenuProps: { sx: { zIndex: 3000 } } }}
                    >
                      <MenuItem value={1}>En ligne</MenuItem>
                      <MenuItem value={0}>Sur RDV</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Commission"
                      name="commission"
                      type="number"
                      fullWidth
                      value={editData.commission || ''}
                      onChange={handleEditChange}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Lien Boutique"
                      name="lien_boutique"
                      fullWidth
                      value={editData.lien_boutique || ''}
                      onChange={handleEditChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Titre"
                      name="title_user"
                      fullWidth
                      value={editData.title_user || ''}
                      onChange={handleEditChange}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Téléphone"
                      name="phone_user"
                      fullWidth
                      value={editData.phone_user || ''}
                      onChange={handleEditChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Adresse"
                      name="addresse_user"
                      fullWidth
                      value={editData.addresse_user || ''}
                      onChange={handleEditChange}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Ville"
                      name="ville_user"
                      fullWidth
                      value={editData.ville_user || ''}
                      onChange={handleEditChange}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="CP"
                      name="cp_user"
                      fullWidth
                      value={editData.cp_user || ''}
                      onChange={handleEditChange}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Province"
                      name="province_user"
                      fullWidth
                      value={editData.province_user || ''}
                      onChange={handleEditChange}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Spécialité"
                      name="main_speciality_user"
                      fullWidth
                      placeholder="ex: Design, Marketing, Web"
                      InputLabelProps={{ shrink: true }}
                      value={editData.main_speciality_user || ''}
                      onChange={handleEditChange}
                      // Texte d'aide identique pour la cohérence
                      helperText="Vous pouvez ajouter plusieurs spécialités en les séparant par une virgule (,)"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          '& fieldset': {
                            borderColor: '#E0E5F2',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#2B3674',
                          fontWeight: 700,
                        },
                        '& .MuiFormHelperText-root': {
                          color: '#A3AED0',
                          fontSize: '0.8rem',
                          marginTop: '4px',
                        },
                      }}
                    />
                  </Grid>{' '}
                  {/* xs={12} force la largeur totale sur mobile et desktop */}
                  <TextField
                    label="Bio"
                    name="description_user"
                    multiline
                    rows={4}
                    fullWidth // Étend le champ sur toute la largeur du Grid item
                    placeholder="Décrivez votre parcours..."
                    InputLabelProps={{ shrink: true }}
                    value={editData.description_user || ''}
                    onChange={handleEditChange}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '16px',
                        backgroundColor: '#FFFFFF',
                        '& fieldset': {
                          borderColor: '#E0E5F2',
                        },
                        '&:hover fieldset': {
                          borderColor: '#4318FF', // Couleur au survol pour le côté pro
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#2B3674',
                        fontWeight: 700,
                      },
                    }}
                  />
                </Grid>
              ) : (
                <Stack spacing={3}>
                  <Box className="detail-card">
                    <Typography
                      sx={{
                        color: '#2B3674',
                        fontWeight: 800,
                        mb: 1,
                        fontSize: '15px',
                      }}
                    >
                      Finance & Boutique
                    </Typography>
                    <DetailItem
                      icon={<PercentIcon />}
                      label="Commission Plateforme"
                      value={`${selectedAdmin.commission || 0}%`}
                      color="#05CD99"
                    />
                    <Divider />
                    <DetailItem
                      icon={<StorefrontIcon />}
                      label="Lien Boutique"
                      value={selectedAdmin.lien_boutique}
                      color="#4318FF"
                    />
                  </Box>

                  <Box className="detail-card">
                    <Typography
                      sx={{
                        color: '#2B3674',
                        fontWeight: 800,
                        mb: 1,
                        fontSize: '15px',
                      }}
                    >
                      Contact & Statut
                    </Typography>
                    <DetailItem
                      icon={<AccessTimeIcon />}
                      label="Statut"
                      value={
                        selectedAdmin.availability_user === 1
                          ? 'En ligne'
                          : 'Sur RDV'
                      }
                      color={
                        selectedAdmin.availability_user === 1
                          ? '#05CD99'
                          : '#EE5D50'
                      }
                    />
                    <Divider />
                    <DetailItem
                      icon={<LocalPhoneIcon />}
                      label="Téléphone"
                      value={selectedAdmin.phone_user}
                    />
                    <Divider />
                    <DetailItem
                      icon={<LocationOnIcon />}
                      label="Localisation"
                      value={`${selectedAdmin.ville_user || ''}, ${selectedAdmin.cp_user || ''}`}
                    />
                    <Divider />
                    <DetailItem
                      icon={<BadgeIcon />}
                      label="Spécialité"
                      value={selectedAdmin.main_speciality_user}
                    />
                    <Divider />
                    <DetailItem
                      icon={<EventNoteIcon />}
                      label="Inscription"
                      value={
                        selectedAdmin.date_inscrip_user
                          ? new Date(
                              selectedAdmin.date_inscrip_user
                            ).toLocaleDateString()
                          : '—'
                      }
                    />
                  </Box>

                  <Box className="detail-card">
                    <Typography
                      sx={{
                        color: '#2B3674',
                        fontWeight: 800,
                        mb: 1,
                        fontSize: '15px',
                      }}
                    >
                      Biographie
                    </Typography>
                    <Typography
                      sx={{
                        color: '#707EAE',
                        fontSize: '14px',
                        lineHeight: 1.6,
                      }}
                    >
                      {selectedAdmin.description_user || 'Aucune description.'}
                    </Typography>
                  </Box>
                </Stack>
              )}
            </Box>

            <Box className="drawer-footer">
              <Button
                fullWidth
                variant={isEditMode ? 'contained' : 'outlined'}
                onClick={
                  isEditMode ? handleUpdateSubmit : () => setIsEditMode(true)
                }
                disabled={isSubmitting}
                sx={{
                  py: 1.5,
                  borderRadius: '12px',
                  fontWeight: 700,
                  bgcolor: isEditMode ? '#4318FF' : 'transparent',
                  color: isEditMode ? 'white' : '#4318FF',
                }}
              >
                {isEditMode
                  ? isSubmitting
                    ? 'Enregistrement...'
                    : 'Sauvegarder'
                  : 'Modifier'}
              </Button>
              {isEditMode && (
                <Button
                  fullWidth
                  sx={{ mt: 1, color: '#EE5D50' }}
                  onClick={() => {
                    setIsEditMode(false);
                    setPreviewUrl(selectedAdmin.photo_user);
                  }}
                >
                  Annuler
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      <Drawer
        anchor="right"
        open={openAddDrawer}
        onClose={() => setOpenAddDrawer(false)}
        sx={{ zIndex: 3000 }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 500 }, p: 3 } }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#2B3674' }}>
            Nouvel Administrateur
          </Typography>
          <IconButton onClick={() => setOpenAddDrawer(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 150px)', pr: 1 }}
        >
          <Stack spacing={2.5}>
            {/* Section Photo */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Avatar
                src={addPreview}
                sx={{
                  width: 100,
                  height: 100,
                  mx: 'auto',
                  mb: 1,
                  border: '2px dashed #4318FF',
                }}
              />
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="add-photo-upload"
                type="file"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setAddFile(file);
                    setAddPreview(URL.createObjectURL(file));
                  }
                }}
              />
              <label htmlFor="add-photo-upload">
                <Button
                  variant="outlined"
                  component="span"
                  size="small"
                  startIcon={<PhotoCamera />}
                >
                  Photo requise
                </Button>
              </label>
            </Box>

            {/* Champs Identité */}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Nom"
                fullWidth
                value={newData.nom_user}
                onChange={(e) =>
                  setNewData({ ...newData, nom_user: e.target.value })
                }
              />
              <TextField
                label="Prénom"
                fullWidth
                value={newData.prenom_user}
                onChange={(e) =>
                  setNewData({ ...newData, prenom_user: e.target.value })
                }
              />
            </Stack>

            <TextField
              label="Email"
              fullWidth
              value={newData.email_user}
              onChange={(e) =>
                setNewData({ ...newData, email_user: e.target.value })
              }
            />
            <TextField
              label="Mot de passe"
              type="password"
              fullWidth
              value={newData.password_user}
              onChange={(e) =>
                setNewData({ ...newData, password_user: e.target.value })
              }
            />

            {/* Champs Localisation & Contact */}
            <TextField
              label="Adresse"
              fullWidth
              value={newData.addresse_user}
              onChange={(e) =>
                setNewData({ ...newData, addresse_user: e.target.value })
              }
            />

            <Stack direction="row" spacing={2}>
              <TextField
                label="Ville"
                fullWidth
                value={newData.ville_user}
                onChange={(e) =>
                  setNewData({ ...newData, ville_user: e.target.value })
                }
              />
              <TextField
                label="Code Postal"
                fullWidth
                value={newData.cp_user}
                onChange={(e) =>
                  setNewData({ ...newData, cp_user: e.target.value })
                }
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField
                label="Province"
                fullWidth
                value={newData.province_user}
                onChange={(e) =>
                  setNewData({ ...newData, province_user: e.target.value })
                }
              />
              <TextField
                label="Téléphone"
                fullWidth
                value={newData.phone_user}
                onChange={(e) =>
                  setNewData({ ...newData, phone_user: e.target.value })
                }
              />
            </Stack>

            {/* Rôles et État */}
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Rôle"
                fullWidth
                value={newData.role_user}
                onChange={(e) =>
                  setNewData({ ...newData, role_user: e.target.value })
                }
              >
                <MenuItem value="1">Super Admin</MenuItem>
                <MenuItem value="2">Admin</MenuItem>
              </TextField>
              <TextField
                select
                label="État"
                fullWidth
                value={newData.etat_user}
                onChange={(e) =>
                  setNewData({ ...newData, etat_user: e.target.value })
                }
              >
                <MenuItem value="active">Actif</MenuItem>
                <MenuItem value="inactive">Inactif</MenuItem>
              </TextField>
            </Stack>

            <Button
              fullWidth
              variant="contained"
              onClick={handleAddSubmit}
              disabled={isSubmitting}
              sx={{
                py: 1.5,
                borderRadius: '12px',
                bgcolor: '#4318FF',
                fontWeight: 700,
                mt: 2,
              }}
            >
              {isSubmitting ? 'Création en cours...' : 'Créer le compte'}
            </Button>
          </Stack>
        </Box>
      </Drawer>

      <Drawer
        anchor="right"
        open={openAddIntervenantDrawer}
        onClose={() => setOpenAddIntervenantDrawer(false)}
        sx={{ zIndex: 3000 }}
        PaperProps={{ sx: { width: { xs: '100%', sm: 600 }, p: 3 } }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#2B3674' }}>
            Nouvel Intervenant
          </Typography>
          <IconButton onClick={() => setOpenAddIntervenantDrawer(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 120px)', pr: 1 }}
        >
          <Stack spacing={2.5}>
            {/* Section Photo */}
            <Box sx={{ textAlign: 'center', mb: 1 }}>
              <Avatar
                src={intervenantPreview}
                sx={{
                  width: 100,
                  height: 100,
                  mx: 'auto',
                  mb: 1,
                  border: '2px dashed #4318FF',
                }}
              />
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="intervenant-photo-upload"
                type="file"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setIntervenantFile(file);
                    setIntervenantPreview(URL.createObjectURL(file));
                  }
                }}
              />
              <label htmlFor="intervenant-photo-upload">
                <Button
                  variant="outlined"
                  component="span"
                  size="small"
                  startIcon={<PhotoCamera />}
                >
                  Photo de profil
                </Button>
              </label>
            </Box>

            {/* Identité */}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Nom"
                fullWidth
                required
                value={intervenantData.nom_user}
                onChange={(e) =>
                  setIntervenantData({
                    ...intervenantData,
                    nom_user: e.target.value,
                  })
                }
              />
              <TextField
                label="Prénom"
                fullWidth
                required
                value={intervenantData.prenom_user}
                onChange={(e) =>
                  setIntervenantData({
                    ...intervenantData,
                    prenom_user: e.target.value,
                  })
                }
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                required
                value={intervenantData.email_user}
                onChange={(e) =>
                  setIntervenantData({
                    ...intervenantData,
                    email_user: e.target.value,
                  })
                }
              />
              <TextField
                label="Mot de passe"
                type="password"
                fullWidth
                required
                value={intervenantData.password_user}
                onChange={(e) =>
                  setIntervenantData({
                    ...intervenantData,
                    password_user: e.target.value,
                  })
                }
              />
            </Stack>

            {/* Titre et Spécialité */}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Titre (ex: Coach Senior)"
                fullWidth
                value={intervenantData.title_user}
                onChange={(e) =>
                  setIntervenantData({
                    ...intervenantData,
                    title_user: e.target.value,
                  })
                }
              />
            </Stack>

            {/* Boutique et Commission */}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Lien Boutique"
                fullWidth
                placeholder="https://..."
                value={intervenantData.lien_boutique}
                onChange={(e) =>
                  setIntervenantData({
                    ...intervenantData,
                    lien_boutique: e.target.value,
                  })
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <StorefrontIcon sx={{ fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Commission (%)"
                type="number"
                fullWidth
                placeholder="20"
                value={intervenantData.commission || ''}
                onChange={(e) =>
                  setIntervenantData({
                    ...intervenantData,
                    commission: e.target.value,
                  })
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              />
            </Stack>

            {/* Contact et Localisation */}
            <TextField
              label="Téléphone"
              fullWidth
              value={intervenantData.phone_user}
              onChange={(e) =>
                setIntervenantData({
                  ...intervenantData,
                  phone_user: e.target.value,
                })
              }
            />

            <TextField
              label="Adresse complète"
              fullWidth
              value={intervenantData.addresse_user}
              onChange={(e) =>
                setIntervenantData({
                  ...intervenantData,
                  addresse_user: e.target.value,
                })
              }
            />

            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  label="Ville"
                  fullWidth
                  value={intervenantData.ville_user}
                  onChange={(e) =>
                    setIntervenantData({
                      ...intervenantData,
                      ville_user: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="CP"
                  fullWidth
                  value={intervenantData.cp_user}
                  onChange={(e) =>
                    setIntervenantData({
                      ...intervenantData,
                      cp_user: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Province/Pays"
                  fullWidth
                  value={intervenantData.province_user}
                  onChange={(e) =>
                    setIntervenantData({
                      ...intervenantData,
                      province_user: e.target.value,
                    })
                  }
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              // Le texte gris qui s'affiche à l'intérieur
              placeholder="Spécialité: Design, Marketing, Web"
              multiline
              rows={3}
              // Force le label à rester en haut (comme sur l'image)
              InputLabelProps={{ shrink: true }}
              value={intervenantData.main_speciality_user}
              onChange={(e) =>
                setIntervenantData({
                  ...intervenantData,
                  main_speciality_user: e.target.value,
                })
              }
              // Texte en bas
              helperText="Vous pouvez ajouter plusieurs spécialités en les séparant par une virgule (,)"
              sx={{
                // Style du champ de saisie (bordures arrondies)
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '& fieldset': {
                    borderColor: '#E0E5F2', // Couleur de bordure douce
                  },
                },
                // Style du label en haut
                '& .MuiInputLabel-root': {
                  color: '#2B3674',
                  fontWeight: 700,
                },
                // Style du helper text (le texte bleu/gris en bas)
                '& .MuiFormHelperText-root': {
                  color: '#A3AED0',
                  fontSize: '0.8rem',
                  marginTop: '8px',
                },
              }}
            />
            {/* Bio et Statut */}
            <TextField
              label="Description / Bio"
              multiline
              rows={3}
              fullWidth
              value={intervenantData.description_user}
              onChange={(e) =>
                setIntervenantData({
                  ...intervenantData,
                  description_user: e.target.value,
                })
              }
            />

            <TextField
              select
              label="État du compte"
              fullWidth
              value={intervenantData.etat_user}
              onChange={(e) =>
                setIntervenantData({
                  ...intervenantData,
                  etat_user: e.target.value,
                })
              }
            >
              <MenuItem value="active">Actif (Accès autorisé)</MenuItem>
              <MenuItem value="inactive">Inactif (Accès bloqué)</MenuItem>
            </TextField>

            <Button
              fullWidth
              variant="contained"
              onClick={handleAddIntervenantSubmit}
              disabled={isSubmitting}
              sx={{
                py: 1.5,
                borderRadius: '12px',
                bgcolor: '#4318FF',
                fontWeight: 700,
                mt: 2,
                '&:hover': { bgcolor: '#3311CC' },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Créer l'Intervenant"
              )}
            </Button>
          </Stack>
        </Box>
      </Drawer>

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Supprimer ce membre ?"
        // On utilise les backticks `` pour insérer les variables
        message={
          adminToDelete
            ? `Êtes-vous sûr de vouloir supprimer ${adminToDelete.prenom_user} ${adminToDelete.nom_user} ? Cette action est irréversible.`
            : ''
        }
        loading={isDeleting}
      />
    </Box>
  );
};

export default Administrator;
